import {
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MarkdownPipe } from '@shared/pipes/markdown/markdown.pipe';
import { SyrfDialogCloseDirective } from '@shared/dialog/syrf-dialog-close.directive';
import {
  SyrfDialogActionsDirective,
  SyrfDialogLayoutComponent,
} from '@shared/dialog/syrf-dialog-layout.component';

/** The heading openers pass as the dialog title. */
export const PREVIEW_PULL_REQUEST_DESCRIPTION_DIALOG_TITLE =
  'Pull request description';

export interface PreviewPullRequestDescriptionDialogData {
  /** Pull request title, shown for context above the description. */
  title: string;
  /** Author-written pull request description, as Markdown source. */
  description: string;
  /** Direct link to the pull request on GitHub, if the PR number was valid. */
  url: string | null;
}

/**
 * Shows the preview environment's pull-request description as rendered
 * Markdown, in its own dialog opened from the environment details dialog.
 *
 * The description is untrusted author input. `MarkdownPipe`'s `untrusted` mode
 * renders it through `marked`, sanitises the result with DOMPurify, and drops
 * elements that would auto-fetch a remote resource (so a crafted description
 * cannot beacon a reader's IP address), and the `[innerHTML]` binding then
 * runs Angular's own DomSanitizer over that output.
 *
 * Close (and Escape or the backdrop) closes with `undefined`; the opener reads
 * no result.
 */
@Component({
  selector: 'app-preview-pull-request-description-dialog',
  imports: [
    SyrfDialogLayoutComponent,
    SyrfDialogActionsDirective,
    SyrfDialogCloseDirective,
    MatButtonModule,
    MatIconModule,
    MarkdownPipe,
  ],
  template: `
    <syrf-dialog-layout>
      <div class="pr-description__content">
        <p class="pr-description__pr-title">{{ data.title }}</p>
        <div
          class="pr-description__markdown"
          [innerHTML]="data.description | markdown: 'untrusted'"
        ></div>
      </div>

      <ng-template syrfDialogActions>
        <div class="pr-description__actions">
          @if (data.url; as prUrl) {
            <a
              class="pr-description__github-link"
              [href]="prUrl"
              target="_blank"
              rel="noopener noreferrer"
            >
              <mat-icon fontSet="fa" fontIcon="fa-github" aria-hidden="true" />
              <span>
                Open pull request on GitHub<mat-icon
                  class="pr-description__new-tab-icon"
                  aria-hidden="true"
                  >open_in_new</mat-icon
                >
                <span class="sr-only">
                  (opens in new tab)
                </span>
              </span>
            </a>
          }
          <button mat-flat-button syrfDialogClose>Close</button>
        </div>
      </ng-template>
    </syrf-dialog-layout>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  // Emulated encapsulation scopes descendant selectors to elements carrying
  // the component's content attribute, and nodes created by [innerHTML] never
  // get it — so every Markdown rule below would be a silent no-op. Every
  // selector here is rooted at a `.pr-description__*` class.
  encapsulation: ViewEncapsulation.None,
  styles: [
    `
      .pr-description__content {
        max-width: 46rem;
      }

      .pr-description__pr-title {
        border-bottom: 1px solid rgba(0, 0, 0, 0.12);
        font-weight: 600;
        line-height: 1.4;
        margin: 0 0 16px;
        overflow-wrap: anywhere;
        padding-bottom: 12px;
      }

      .pr-description__markdown {
        font-size: 14px;
        line-height: 1.55;
        overflow-wrap: anywhere;

        > :first-child {
          margin-top: 0;
        }

        > :last-child {
          margin-bottom: 0;
        }

        h1,
        h2,
        h3,
        h4 {
          font-weight: 600;
          line-height: 1.3;
          margin: 20px 0 8px;
        }

        h1 {
          font-size: 18px;
        }

        h2 {
          font-size: 16px;
        }

        h3,
        h4 {
          font-size: 15px;
        }

        p,
        ul,
        ol,
        blockquote,
        pre,
        table {
          margin: 0 0 12px;
        }

        ul,
        ol {
          padding-left: 24px;
        }

        li + li {
          margin-top: 4px;
        }

        code {
          background: rgba(0, 0, 0, 0.06);
          border-radius: 4px;
          font-size: 0.9em;
          padding: 1px 4px;
        }

        pre {
          background: rgba(0, 0, 0, 0.06);
          border-radius: 6px;
          overflow-x: auto;
          padding: 12px;

          code {
            background: none;
            padding: 0;
          }
        }

        blockquote {
          border-left: 2px solid rgba(0, 0, 0, 0.16);
          color: #555;
          padding-left: 12px;
        }

        hr {
          border: none;
          border-top: 1px solid rgba(0, 0, 0, 0.12);
          margin: 16px 0;
        }

        img {
          max-width: 100%;
        }

        table {
          border-collapse: collapse;
          display: block;
          max-width: 100%;
          overflow-x: auto;

          th,
          td {
            border: 1px solid rgba(0, 0, 0, 0.12);
            padding: 6px 10px;
            text-align: left;
          }

          th {
            background: rgba(0, 0, 0, 0.04);
          }
        }

        /* Task-list markers; see renderTaskMarkers in markdown.pipe.ts. */
        .markdown-task {
          font-size: 1.1em;
          margin-right: 6px;
        }

        .markdown-task--done {
          color: #2e7d32;
        }
      }

      .pr-description__actions {
        align-items: center;
        border-top: 1px solid rgba(0, 0, 0, 0.12);
        column-gap: 16px;
        display: flex;
        flex: 1;
        flex-wrap: wrap;
        justify-content: flex-end;
        padding-top: 12px;
        row-gap: 8px;
      }

      .pr-description__github-link {
        align-items: flex-start;
        display: inline-flex;
        gap: 6px;
        /* Keeps Close hard right on one line, and right on its own line
           when the link wraps at narrow widths. */
        margin-right: auto;
        text-align: left;
        text-decoration: none;

        &:hover,
        &:focus-visible {
          text-decoration: underline;
        }

        > mat-icon {
          flex-shrink: 0;
          font-size: 18px;
          height: 18px;
          margin-top: 2px;
          width: 18px;
        }
      }

      /* Inline so it follows the last word when the label wraps. */
      .pr-description__new-tab-icon {
        font-size: 16px;
        height: 16px;
        margin-left: 4px;
        opacity: 0.7;
        vertical-align: text-bottom;
        width: 16px;
      }

    `,
  ],
})
export class PreviewPullRequestDescriptionDialogComponent {
  protected readonly data = inject<PreviewPullRequestDescriptionDialogData>(
    MAT_DIALOG_DATA
  );
}
